import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { addNote, CASE_GONE_SENTENCE, CASE_KEY, CASES_KEY, caseGone, ProblemError, RECORD_KEY } from "@/api";
import { Button } from "@/components/Button";
import { TextArea } from "@/components/fields/TextArea";
import { TextField } from "@/components/fields/TextField";
import { StateMessage } from "@/components/StateMessage";
import { localDateTimeValue } from "@/lib/format";
import { firstChordCaps, isMac } from "@/lib/shortcuts";

import styles from "./Record.module.css";

/** „Strg Enter" (⌘ on a Mac) — from the registry the „?" overview lists. */
function fileKeys(): string {
  return firstChordCaps("noteFile", isMac()).join(" ");
}

interface NoteFormProps {
  caseId: string;
  /** Lives in the record, not here: Escape closes the form, the draft waits (UX ruling 7). */
  draft: string;
  onDraft: (text: string) => void;
  onAnnounce: (sentence: string) => void;
  onFiled: (entryId: string) => void;
  onClose: () => void;
}

/**
 * A note in the record — a phone call, an agreement (design §8.2). Dated now
 * unless the person sets „Zeitpunkt" („Telefonat gestern", UX ruling 7);
 * only a changed time goes on the wire (`occurredAt`). ⌘/Strg + Enter files
 * it, Escape closes and keeps the draft — from anywhere in the form, the
 * time field included (record fix wave, T8). Blank and NUL-only text is
 * refused at the field before the server would (422, Plan B).
 *
 * While the note goes out its button keeps the focus, `aria-disabled` and
 * „Wird abgelegt …" (record fix wave I1) — `disabled` dropped the focus to
 * `<body>`; a second click or ⌘/Strg + Enter does nothing meanwhile.
 *
 * A Vorgang handed over while the note was written answers 404 (final
 * review of Plan D, I-3): the Vorgang is read again — the record turns into
 * „nicht gefunden" — and until then the form says why and holds its button,
 * with that sentence as the reason. „Abbrechen" stays.
 */
export function NoteForm({ caseId, draft, onDraft, onAnnounce, onFiled, onClose }: NoteFormProps) {
  const client = useQueryClient();
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const [opened] = useState(() => localDateTimeValue());
  const [when, setWhen] = useState(opened);
  const [timed, setTimed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [gone, setGone] = useState(false);

  async function send() {
    if (busy || gone) return;
    const note = draft.split("\u0000").join("").trim();
    if (note === "") {
      setError("Die Notiz ist leer.");
      fieldRef.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    setRefused(null);
    try {
      const entry = timed
        ? await addNote(caseId, note, new Date(when).toISOString())
        : await addNote(caseId, note);
      onDraft("");
      onAnnounce("Notiz abgelegt.");
      onFiled(entry.id);
      void client.invalidateQueries({ queryKey: RECORD_KEY(caseId) });
      // `["cases"]`, not only the register's overview: the start page's list
      // names the last activity too (record fix round 2, M-5).
      void client.invalidateQueries({ queryKey: CASES_KEY });
      onClose();
    } catch (cause) {
      setBusy(false);
      if (caseGone(cause)) {
        setGone(true);
        setRefused(CASE_GONE_SENTENCE);
        void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
        return;
      }
      setRefused(cause instanceof ProblemError ? cause.userMessage : "Notiz nicht abgelegt — Server nicht erreichbar.");
    }
  }

  const held = busy || gone ? "true" : undefined;

  return (
    <form
      className={styles.noteForm}
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Escape" || busy) return;
        event.preventDefault();
        onClose();
      }}
    >
      <TextArea
        id="note-text"
        label="Notiz"
        value={draft}
        onChange={(text) => {
          setError(null);
          onDraft(text);
        }}
        rows={4}
        maxLength={20000}
        autoFocus
        textareaRef={fieldRef}
        error={error}
        hint={`${fileKeys()} legt ab`}
        onKeyDown={(event) => {
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            void send();
          }
        }}
      />
      <div className={styles.noteWhen}>
        <TextField
          id="note-when"
          label="Zeitpunkt"
          type="datetime-local"
          value={when}
          max={opened}
          onChange={(value) => {
            setTimed(true);
            setWhen(value);
          }}
        />
      </div>
      {/* The server's refusal in the one notice pattern (final review of
          Plan D) — red edge, symbol and word, the only `role="alert"` here. */}
      {refused !== null ? <StateMessage id="note-refused" tone="conflict" live="alert" title={refused} /> : null}
      <div className={styles.actions}>
        <Button onClick={onClose} aria-disabled={busy ? "true" : undefined}>
          Abbrechen
        </Button>
        {/* Ink, not red (design 2026-10-24 §8.2): the record's one red
            action is the way on in its head; the note is filed inside the
            history, a second strong action beside it. */}
        <Button
          type="submit"
          variant="tinte"
          aria-disabled={held}
          aria-describedby={gone ? "note-refused" : undefined}
        >
          {busy ? "Wird abgelegt …" : "Notiz ablegen"}
        </Button>
      </div>
    </form>
  );
}
